import { useState } from "react";
import { Chip, Stack, TextField, Typography } from "@mui/material";
import BaseModal from "@/pages/InvoiceManagement/v2/components/BaseModal";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { DeclineRfqPayload, RfqDeclineReason, VendorRfqDetail } from "../../types";
import { DECLINE_REASONS } from "../../constants/declineReasons";
import { tradesOf } from "../../utils/response";

const REMARKS_MAX = 500;

interface DeclineRfqModalProps {
  open: boolean;
  detail: VendorRfqDetail;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (payload: DeclineRfqPayload) => void;
}

export default function DeclineRfqModal({ open, detail, submitting, onClose, onConfirm }: DeclineRfqModalProps) {
  const [reason, setReason] = useState<RfqDeclineReason | null>(null);
  const [remarks, setRemarks] = useState("");
  const lineCount = detail.lines.length;
  const needsRemarks = reason === "OTHER";
  const trimmed = remarks.trim();

  const close = () => {
    if (submitting) return;
    setReason(null);
    setRemarks("");
    onClose();
  };

  const confirm = () => {
    if (!reason) return;
    onConfirm(trimmed ? { reason, remarks: trimmed } : { reason });
  };

  return (
    <BaseModal
      open={open}
      title="Decline this RFQ?"
      confirmLabel="Yes, decline RFQ"
      cancelLabel="Keep bidding"
      confirmColor={V2_COLORS.error}
      confirmDisabled={!reason || (needsRemarks && !trimmed)}
      loading={submitting}
      onClose={close}
      onConfirm={confirm}
    >
      <Typography fontSize="13px">
        {`An RFQ is accepted or declined as a whole — declining passes on all ${lineCount} line item${lineCount === 1 ? "" : "s"}. The Supply Manager will offer it to the next vendor in the pool.`}
      </Typography>
      <Typography fontSize="12px" sx={{ color: V2_COLORS.textSecondary, mt: 1 }}>
        {tradesOf(detail)}
      </Typography>
      <Typography fontSize="10px" sx={{ color: V2_COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", mt: 2 }}>
        Reason · required
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
        {DECLINE_REASONS.map((option) => {
          const selected = option.code === reason;
          return (
            <Chip
              key={option.code}
              label={option.label}
              clickable
              aria-pressed={selected}
              onClick={() => setReason(option.code)}
              variant={selected ? "filled" : "outlined"}
              color={selected ? "primary" : "default"}
              disabled={submitting}
            />
          );
        })}
      </Stack>
      <TextField
        label={needsRemarks ? "Remarks (required for Other)" : "Remarks (optional)"}
        value={remarks}
        onChange={(event) => setRemarks(event.target.value)}
        helperText={`${remarks.length}/${REMARKS_MAX} characters`}
        required={needsRemarks}
        multiline
        minRows={2}
        fullWidth
        size="small"
        disabled={submitting}
        inputProps={{ maxLength: REMARKS_MAX }}
        sx={{ mt: 2 }}
      />
    </BaseModal>
  );
}
